<template>
  <div style="margin:20px">

    <div style="width: 100%; background-color: #eee; border: 1px dotted grey; margin-bottom: 25px;">
      <span style="font-size: 26px; font-weight: bold">All Tenants</span><br/>
      <span style="font-style: italic; font-size: 18px">this page provides an overview on all accounts in the system</span>
    </div>

    <div class="tenant-toolbar" v-if="!loading">
      <div class="tenant-filters-row">
        <span @click="filter_by_status(null,null)" class="tenant-filter-chip tenant-filter-clickable">{{items.length}}/{{items_full.length}}</span>
        <span class="tenant-filter-sep">|</span>
        <span class="tenant-filter-label">Counts</span>
        <span class="tenant-filter-chip">Vehicles: {{vehicle_cnt}}</span>
        <span class="tenant-filter-chip">Drivers: {{driver_cnt}}</span>
        <span class="tenant-filter-sep">|</span>
        <span class="tenant-filter-label">Booking Platform</span>
        <span @click="filter_by_status('tenant', 'CM')" :class="tenant_type_badge_classes('CM')">CM: {{cm_cnt}}</span>
        <span @click="filter_by_status('tenant', 'TBN')" :class="tenant_type_badge_classes('TBN')">TBN: {{tbn_cnt}}</span>
        <span @click="filter_by_status('tenant', 'FASTTRAK')" :class="tenant_type_badge_classes('FASTTRAK')">FASTTRAK: {{fasttrak_cnt}}</span>
        <span class="tenant-filter-sep">|</span>
        <span class="tenant-filter-label">Telematics</span>
        <span @click="filter_by_status('telematics', 'SAMSARA')" :class="telematics_type_badge_classes('SAMSARA')">SAMSARA: {{samsara_cnt}}</span>
        <span @click="filter_by_status('telematics', 'SAUCON')" :class="telematics_type_badge_classes('SAUCON')">SAUCON: {{saucon_cnt}}</span>
        <span @click="filter_by_status('telematics', 'ZONAR')" :class="telematics_type_badge_classes('ZONAR')">ZONAR: {{zonar_cnt}}</span>
        <span @click="filter_by_status('telematics', 'MOTIVE')" :class="telematics_type_badge_classes('MOTIVE')">MOTIVE: {{motive_cnt}}</span>
        <span @click="filter_by_status('telematics', 'NONE')" :class="telematics_type_badge_classes('NONE')">NONE: {{none_cnt}}</span>
      </div>
      <div class="tenant-feature-filters-row">
        <span class="tenant-filter-label">Feature Filters</span>
        <template v-for="(label, key) in feature_filters">
          <b-button
            v-if="feature_count(key) > 0"
            :key="key"
            size="sm"
            class="tenant-feature-btn"
            :class="{ 'tenant-feature-btn-selected': selected_feature === key }"
            variant="success"
            :title="key"
            @click="filter_by_status('feature', key)"
          >{{ label }}: {{ feature_count(key) }}</b-button>
        </template>
      </div>
      <div class="tenant-search-row">
        <b-form-input class="tenant-search-input" v-model="results_filter" placeholder="text or key=value (e.g. data_services_compare_drivers_enabled=True)"/>
        <b-form-select class="tenant-setting-key-select" v-model="setting_filter_key" :options="enabled_setting_options"/>
        <b-form-select class="tenant-setting-value-select" v-model="setting_filter_value" :options="setting_filter_value_options" :disabled="!setting_filter_key"/>
      </div>
    </div>

      <b-table class="tenant-manager-table" :busy="loading" sticky-header="80vh" striped bordered :items="items" :fields="fields" responsive
             :current-page="currentPage"
             :per-page="perPage">

      <template #table-busy>
        <div class="text-center text-danger my-2">
          <b-spinner class="align-middle"></b-spinner>
        </div>
      </template>

        <template slot="last_actuals_request_time" slot-scope="row">
          {{row.item.last_actuals_request_time | formatDateTime}}
        </template>

        <template slot="last_booking_update_time" slot-scope="row">
          {{row.item.last_booking_update_time | formatDateTime}}
        </template>

        <template slot="tenant_type" slot-scope="row">
          <span
            v-if="row.item.tenant_type"
            @click.stop="filter_by_status('tenant', row.item.tenant_type)"
            :class="tenant_type_badge_classes(row.item.tenant_type)"
          >{{ row.item.tenant_type }}</span>
          <span v-else>—</span>
        </template>

        <template slot="telematics_type" slot-scope="row">
          <span
            v-if="row.item.telematics_type"
            @click.stop="filter_by_status('telematics', row.item.telematics_type)"
            :class="telematics_type_badge_classes(row.item.telematics_type)"
          >{{ row.item.telematics_type }}</span>
          <span v-else>—</span>
        </template>

        <template slot="users" slot-scope="row">
          <table class="tenant-users-table tenant-users-table-no-hover" cellpadding="0" cellspacing="0">
            <thead>
              <tr>
                <th>Username</th>
                <th>Name</th>
                <th>Title</th>
                <th>Last Login</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="u in sorted_customer_admins(row.item.users)" :key="u.username || u.id">
                <td class="tenant-user-username">{{ u.username || '—' }}</td>
                <td class="tenant-user-name">{{ u.first_name }} {{ u.last_name }}</td>
                <td>{{ u.job_title || '—' }}</td>
                <td class="tenant-user-login">{{ format_last_login(u.last_login_time) }}</td>
              </tr>
            </tbody>
          </table>
        </template>

        <template slot="features" slot-scope="row">
          <div class="tenant-features-cell">
            <template v-for="(label, key) in feature_filters">
              <b-button
                v-if="tenant_feature_enabled(row.item, key)"
                :key="key"
                size="sm"
                class="tenant-feature-btn"
                :class="{ 'tenant-feature-btn-selected': selected_feature === key }"
                variant="success"
                :title="key"
                @click.stop="filter_by_status('feature', key)"
              >{{ label }}</b-button>
            </template>
          </div>
        </template>

      <template slot="details" slot-scope="row">
        <b-button size="sm" @click="row.toggleDetails">
          {{ row.detailsShowing ? '-' : '+' }}
        </b-button>&nbsp;
      </template>

      <template slot="row-details" slot-scope="row">
        <b-card style="text-align: left;">
          <div style='min: 1500px; display: flex; flex-wrap: wrap'>
            <pre style="padding: 10px; background-color: white; text-align: left">{{row.item}}</pre>
          </div>
        </b-card>
      </template>

    </b-table>

  </div>
</template>
<script>
import {delete_booking, delete_tenant, get_all_tenants_config, send_sos_request} from '../networking/apis'
    import {mapState} from "vuex";
    import moment from 'moment'

    export default {
      name: 'tenant_manager',
      data() {
        return {
          items: [],
          items_full: [],
          summary: {},
          loading: true,
          results_filter: null,
          setting_filter_key: null,
          setting_filter_value: null,
          selected_tenant_type: null,
          selected_telematics_type: null,
          selected_feature: null,
          feature_filters: {
            actuals_enabled: 'Actuals',
            alerts_enabled: 'Alerts',
            views_reports_enabled: 'Reports',
            vehicle_status_management_enabled: 'VSM',
            data_services_enabled: 'Data',
            routes_enabled: 'Routes',
            bus_sos_enabled: 'SOS',
            late_alert_enabled: 'Late Alts (old)',
            vehicle_late_alert_enabled: 'Late Alts (new)',
            driver_trip_notifications_enabled: 'Driver Pre-Trips',
            customer_trip_notifications_enabled: 'Cust Pre-Trips',
            customer_post_trip_notifications_enabled: 'Cust Post-Trips',
          },
          boolean_options: [
            { text: 'True', value: 'True' },
            { text: 'False', value: 'False' },
          ],
          totalRows: 1,
          currentPage: 1,
          perPage: 100,
          pages: [],
          fields: [
            { key: 'details', label: '', sortable: false, thStyle: { width: "20px" }},
            { key: 'tenant_id', sortable: true, thStyle: { width: "110px" }, tdClass: 'tenant-data-td', thClass: 'tenant-data-th' },
            { key: 'tenant_name', sortable: true, thStyle: { width: "150px" }, tdClass: 'tenant-data-td', thClass: 'tenant-data-th' },
            { key: 'tenant_type', label: 'Ops', sortable: true, thStyle: { width: "90px" }, tdClass: 'tenant-data-td', thClass: 'tenant-data-th' },
            { key: 'telematics_type', label: 'Telematics', sortable: true, thStyle: { width: "110px" }, tdClass: 'tenant-data-td', thClass: 'tenant-data-th' },
            { key: 'last_actuals_request_time', label: 'Last Actuals Push', sortable: true, thStyle: { width: "170px" }, tdClass: 'tenant-data-td', thClass: 'tenant-data-th' },
            { key: 'last_booking_update_time', label: 'Last Booking Update', sortable: true, thStyle: { width: "180px" }, tdClass: 'tenant-data-td', thClass: 'tenant-data-th' },
            { key: 'vehicle_count', label: 'Vehicles', sortable: true, thStyle: { width: "60px" }, tdClass: 'tenant-data-td', thClass: 'tenant-data-th' },
            { key: 'driver_count', label: 'Drivers', sortable: true, thStyle: { width: "60px" }, tdClass: 'tenant-data-td', thClass: 'tenant-data-th' },
            { key: 'features', sortable: true, thStyle: { width: "260px" }, tdClass: 'tenant-features-td', thClass: 'tenant-features-th' },
            { key: 'users', sortable: true, thStyle: { width: "580px" }, tdClass: 'tenant-users-td', thClass: 'tenant-users-th' },
          ]

        }
      },
      async created() {
        this.load_data()
      },
      methods: {
        async load_data() {
          this.loading = true
          try {
            const {value: { data = [] } } = await get_all_tenants_config()
            this.summary = data.summary
            this.items = data.results
            this.items_full = data.results
          } catch(e) {
            this.$emit('showAlert', 'error retrieving data, try logging in again or contact support if needed', 'danger', 5000)
          }

          this.loading = false
        },
        tenant_type_badge_classes(value) {
          const classes = ['tenant-value-badge', 'tenant-filter-clickable']
          if (value === 'CM')
            classes.push('tenant-type-cm')
          else if (value === 'TBN')
            classes.push('tenant-type-tbn')
          else if (value === 'FASTTRAK')
            classes.push('tenant-type-fasttrak')
          if (this.selected_tenant_type === value)
            classes.push('selected')
          return classes
        },
        telematics_type_badge_classes(value) {
          const classes = ['tenant-value-badge', 'tenant-filter-clickable']
          if (value === 'SAMSARA')
            classes.push('tenant-telematics-samsara')
          else if (value === 'SAUCON')
            classes.push('tenant-telematics-saucon')
          else if (value === 'ZONAR')
            classes.push('tenant-telematics-zonar')
          else if (value === 'MOTIVE')
            classes.push('tenant-telematics-motive')
          else if (value === 'NONE')
            classes.push('tenant-telematics-none')
          if (this.selected_telematics_type === value)
            classes.push('selected')
          return classes
        },
        filter_by_status(type, value) {
          if (type == null) {
            this.selected_tenant_type = null
            this.selected_telematics_type = null
            this.selected_feature = null
          }

          if (type == 'tenant') {
            if (this.selected_tenant_type == value)
              this.selected_tenant_type = null
            else
              this.selected_tenant_type = value
          }

          if (type == 'telematics') {
            if (this.selected_telematics_type == value)
              this.selected_telematics_type = null
            else
              this.selected_telematics_type = value
          }

          if (type == 'feature') {
            if (this.selected_feature == value)
              this.selected_feature = null
            else
              this.selected_feature = value
          }

          this.apply_filters()
        },
        apply_filters() {
          let items = this.items_full

          if (this.selected_tenant_type)
            items = items.filter(item => item.tenant_type == this.selected_tenant_type)

          if (this.selected_telematics_type)
            items = items.filter(item => item.telematics_type == this.selected_telematics_type)

          if (this.selected_feature)
            items = items.filter(item => this.tenant_has_key_value(item, this.selected_feature, 'True'))

          if (this.results_filter && this.results_filter.trim()) {
            const val = this.results_filter.trim()
            items = items.filter(item => this.tenant_matches_filter(item, val))
          }

          if (this.setting_filter_key) {
            const key = this.setting_filter_key
            const value = this.setting_filter_value && this.setting_filter_value.trim()
            if (value)
              items = items.filter(item => this.tenant_has_key_value(item, key, value))
            else
              items = items.filter(item => this.tenant_has_key(item, key))
          }

          this.items = items
        },
        filter_results() {
          this.apply_filters()
        },
        feature_count(key) {
          return this.feature_count_base_items.filter(item => this.tenant_has_key_value(item, key, 'True')).length
        },
        format_last_login(value) {
          if (value == null || value === '' || value === 0 || value === '0')
            return '—'

          if (typeof value === 'string' && value.indexOf('T') !== -1) {
            const m = moment(value)
            return m.isValid() ? m.format('MM/DD/YY hh:mm:ss A') : '—'
          }

          const n = Number(value)
          if (!Number.isNaN(n) && n > 0) {
            const m = n < 1e12 ? moment.unix(n) : moment(n)
            return m.isValid() ? m.format('MM/DD/YY hh:mm:ss A') : '—'
          }

          const m = moment(String(value))
          return m.isValid() ? m.format('MM/DD/YY hh:mm:ss A') : '—'
        },
        tenant_feature_enabled(item, key) {
          return this.tenant_has_key_value(item, key, 'True')
        },
        sorted_customer_admins(users) {
          if (!users)
            return []
          return users
            .filter(u => u.role == 'customer-admin')
            .slice()
            .sort((a, b) => {
              const byFirst = (a.first_name || '').localeCompare(b.first_name || '', undefined, { sensitivity: 'base' })
              if (byFirst !== 0)
                return byFirst
              return (a.last_name || '').localeCompare(b.last_name || '', undefined, { sensitivity: 'base' })
            })
        },
        tenant_matches_filter(item, filter) {
          const eqIndex = filter.indexOf('=')
          if (eqIndex > 0) {
            const key = filter.substring(0, eqIndex).trim()
            const value = filter.substring(eqIndex + 1).trim()
            return this.tenant_has_key_value(item, key, value)
          }
          return this.contains(item.tenant_name, filter)
            || this.contains(item.aws_phone_number, filter)
            || this.tenant_contains_text(item, filter)
        },
        tenant_has_key(obj, searchKey) {
          if (obj == null || typeof obj !== 'object')
            return false

          const normalizedKey = searchKey.toLowerCase()

          if (Array.isArray(obj))
            return obj.some(el => this.tenant_has_key(el, searchKey))

          for (const [key, value] of Object.entries(obj)) {
            if (key.toLowerCase() === normalizedKey)
              return true
            if (value != null && typeof value === 'object' && this.tenant_has_key(value, searchKey))
              return true
          }
          return false
        },
        tenant_has_key_value(obj, searchKey, searchValue) {
          if (obj == null || typeof obj !== 'object')
            return false

          const normalizedKey = searchKey.toLowerCase()

          if (Array.isArray(obj))
            return obj.some(el => this.tenant_has_key_value(el, searchKey, searchValue))

          for (const [key, value] of Object.entries(obj)) {
            if (key.toLowerCase() === normalizedKey && this.values_match(value, searchValue))
              return true
            if (value != null && typeof value === 'object' && this.tenant_has_key_value(value, searchKey, searchValue))
              return true
          }
          return false
        },
        values_match(actual, expected) {
          if (actual == null)
            return expected === '' || expected.toLowerCase() === 'null' || expected.toLowerCase() === 'none'

          const expectedNorm = expected.toLowerCase()
          if (expectedNorm === 'true' || expectedNorm === 'false') {
            const expectedBool = expectedNorm === 'true'
            if (typeof actual === 'boolean')
              return actual === expectedBool
            if (typeof actual === 'string')
              return actual.toLowerCase() === expectedNorm
          }

          return String(actual).toLowerCase() === expectedNorm
        },
        tenant_contains_text(obj, text) {
          if (obj == null)
            return false

          const lower = text.toLowerCase()
          if (typeof obj === 'string' || typeof obj === 'number' || typeof obj === 'boolean')
            return String(obj).toLowerCase().indexOf(lower) !== -1

          if (Array.isArray(obj))
            return obj.some(el => this.tenant_contains_text(el, text))

          if (typeof obj === 'object')
            return Object.values(obj).some(v => this.tenant_contains_text(v, text))

          return false
        },
        contains(val1, val2) {
          if (val1 != null) {
            return val1.toLowerCase().indexOf(val2.toLowerCase()) != -1;
          }
          return false;
        },
        // list_contains(records, field, val2) {
        //   for (let record in records) {
        //     console.log('user:' + record)
        //     if (record[field] != null) {
        //       return record[field].toLowerCase().indexOf(val2.toLowerCase()) != -1;
        //     }
        //   }
        //   return false;
        // }
      } ,
      computed:{
        ...mapState({
          user: state => state.profile.user
        }),
        vehicle_cnt() {
          this.veh_items = this.items.filter(item => item.vehicle_count)
          const totalScore = this.veh_items.reduce(function (a, b) {
            return { vehicle_count: Number(a.vehicle_count) + Number(b.vehicle_count) };
          });
          return totalScore.vehicle_count;
        },
        driver_cnt() {
          this.veh_items = this.items.filter(item => item.driver_count)
          const totalScore = this.veh_items.reduce(function (a, b) {
            return { driver_count: Number(a.driver_count) + Number(b.driver_count) };
          });
          return totalScore.driver_count;
        },
        feature_count_base_items() {
          let items = this.items_full
          if (this.selected_tenant_type)
            items = items.filter(item => item.tenant_type == this.selected_tenant_type)
          if (this.selected_telematics_type)
            items = items.filter(item => item.telematics_type == this.selected_telematics_type)
          return items
        },
        fasttrak_cnt() {
          return this.items_full.filter(item => item.tenant_type == 'FASTTRAK').length
        },
        cm_cnt() {
          return this.items_full.filter(item => item.tenant_type == 'CM').length
        },
        tbn_cnt() {
          return this.items_full.filter(item => item.tenant_type == 'TBN').length
        },
        samsara_cnt() {
          return this.items_full.filter(item => item.telematics_type == 'SAMSARA').length
        },
        saucon_cnt() {
          return this.items_full.filter(item => item.telematics_type == 'SAUCON').length
        },
        zonar_cnt() {
          return this.items_full.filter(item => item.telematics_type == 'ZONAR').length
        },
        motive_cnt() {
          return this.items_full.filter(item => item.telematics_type == 'MOTIVE').length
        },
        none_cnt() {
          return this.items_full.filter(item => item.telematics_type == 'NONE').length
        },
        setting_filter_value_options() {
          return [{ value: null, text: '-- value --' }].concat(this.boolean_options)
        },
        enabled_setting_options() {
          const keys = new Set()
          const collect = (obj) => {
            if (obj == null || typeof obj !== 'object')
              return
            if (Array.isArray(obj)) {
              obj.forEach(collect)
              return
            }
            for (const [key, value] of Object.entries(obj)) {
              if (key.endsWith('_enabled'))
                keys.add(key)
              if (value != null && typeof value === 'object')
                collect(value)
            }
          }
          this.items_full.forEach(item => collect(item))
          const sorted = Array.from(keys).sort((a, b) => a.localeCompare(b))
          return [{ value: null, text: '-- setting --' }].concat(
            sorted.map(k => ({ value: k, text: k }))
          )
        },
      },
      watch: {
        results_filter() {
          this.filter_results()
        },
        setting_filter_key() {
          this.apply_filters()
        },
        setting_filter_value() {
          this.apply_filters()
        },
      }
    }
</script>
<style scoped>
.tenant-toolbar {
  margin: 20px;
}
.tenant-filters-row,
.tenant-feature-filters-row,
.tenant-search-row {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: center;
  gap: 4px 6px;
  font-weight: bold;
  margin-bottom: 20px;
}
.tenant-filters-row {
  font-size: 16px;
}
.tenant-feature-filters-row {
  font-size: 15px;
}
.tenant-feature-filters-row .tenant-filter-label {
  font-size: 16px;
}
.tenant-search-row {
  font-size: 13px;
}
.tenant-filter-label {
  margin-right: 4px;
  white-space: nowrap;
}
.tenant-filter-chip {
  border: 1px solid grey;
  padding: 2px 6px;
  white-space: nowrap;
  line-height: 1.3;
}
.tenant-value-badge {
  display: inline-block;
  border: 1px solid transparent;
  border-radius: 3px;
  padding: 2px 8px;
  white-space: nowrap;
  line-height: 1.3;
  font-weight: bold;
}
.tenant-type-cm {
  background-color: #0d6efd;
  border-color: #0d6efd;
  color: #ffffff;
}
.tenant-type-tbn {
  background-color: #fd7e14;
  border-color: #fd7e14;
  color: #ffffff;
}
.tenant-type-fasttrak {
  background-color: #6f42c1;
  border-color: #6f42c1;
  color: #ffffff;
}
.tenant-telematics-samsara {
  background-color: #20c997;
  border-color: #20c997;
  color: #ffffff;
}
.tenant-telematics-saucon {
  background-color: #17a2b8;
  border-color: #17a2b8;
  color: #ffffff;
}
.tenant-telematics-zonar {
  background-color: #d63384;
  border-color: #d63384;
  color: #ffffff;
}
.tenant-telematics-motive {
  background-color: #ffc107;
  border-color: #e0a800;
  color: #212529;
}
.tenant-telematics-none {
  background-color: #6c757d;
  border-color: #6c757d;
  color: #ffffff;
}
.tenant-value-badge.selected {
  border: 2px solid red !important;
  padding: 1px 7px;
}
.tenant-feature-filters-row >>> .tenant-feature-btn.btn,
.tenant-features-cell >>> .tenant-feature-btn.btn {
  padding: 2px 7px !important;
  margin: 0 !important;
  font-size: 15px !important;
  font-weight: bold !important;
  line-height: 1.25 !important;
  min-height: 0 !important;
  height: auto !important;
  opacity: 1;
  cursor: pointer;
}
.tenant-feature-filters-row >>> .tenant-feature-btn-selected.btn,
.tenant-features-cell >>> .tenant-feature-btn-selected.btn {
  border: 2px solid red !important;
  box-shadow: none !important;
}
.tenant-filter-chip.selected {
  border: 2px solid red;
  padding: 1px 5px;
}
.tenant-value-badge.tenant-filter-clickable {
  cursor: pointer;
}
.tenant-manager-table >>> tbody tr:nth-of-type(even) td {
  background-color: #ececec !important;
}
.tenant-manager-table >>> tbody tr:nth-of-type(odd) td {
  background-color: #ffffff !important;
}
.tenant-manager-table >>> tbody tr:nth-of-type(even):hover td:not(.tenant-users-td) {
  background-color: #ececec !important;
}
.tenant-manager-table >>> tbody tr:nth-of-type(odd):hover td:not(.tenant-users-td) {
  background-color: #ffffff !important;
}
.tenant-manager-table >>> tbody tr:hover td.tenant-users-td {
  background-color: #ffffff !important;
}
.tenant-filter-clickable {
  cursor: pointer;
}
.tenant-filter-sep {
  color: #999;
  padding: 0 1px;
  user-select: none;
}
.tenant-search-row {
  gap: 8px;
  width: 100%;
}
.tenant-search-input {
  flex: 0 0 600px;
  width: 600px;
  max-width: 100%;
}
.tenant-setting-key-select {
  flex: 0 1 320px;
  width: 320px;
  max-width: 100%;
}
.tenant-setting-value-select {
  flex: 0 0 110px;
  width: 110px;
}
.tenant-data-td {
  max-width: none !important;
  white-space: nowrap;
}
.tenant-features-th {
  width: 260px;
}
.tenant-features-td {
  max-width: 260px !important;
  white-space: normal !important;
  vertical-align: top;
}
.tenant-features-cell {
  display: flex;
  flex-wrap: wrap;
  align-content: flex-start;
  gap: 3px 4px;
  width: 100%;
  max-width: 260px;
}
.tenant-users-th {
  width: 580px;
}
.tenant-manager-table >>> .tenant-users-td {
  background-color: #ffffff !important;
}
.tenant-users-td {
  max-width: none !important;
  white-space: normal !important;
  vertical-align: top;
  background-color: #ffffff !important;
}
.tenant-users-table {
  width: 100%;
  font-size: 14px;
  line-height: 1.3;
  background-color: #ffffff;
  border-collapse: collapse;
}
.tenant-users-table th {
  font-weight: bold;
  font-size: 15px;
  text-align: center;
  padding: 0 6px 2px 6px;
  white-space: nowrap;
  background-color: #ffffff !important;
}
.tenant-users-table td {
  padding: 1px 6px 1px 0;
  vertical-align: top;
  max-width: none;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.tenant-users-table tbody tr:nth-of-type(odd) td {
  background-color: #ffffff !important;
}
.tenant-users-table tbody tr:nth-of-type(even) td {
  background-color: rgba(144, 238, 144, 0.5) !important;
}
.tenant-manager-table >>> .tenant-users-table-no-hover tbody tr:hover,
.tenant-users-table-no-hover tbody tr:hover {
  background-color: transparent !important;
  background-image: none !important;
  box-shadow: none !important;
}
.tenant-manager-table >>> .tenant-users-table-no-hover tbody tr:nth-of-type(odd):hover > td,
.tenant-manager-table >>> .tenant-users-table-no-hover tbody tr:nth-of-type(odd):hover td,
.tenant-users-table-no-hover tbody tr:nth-of-type(odd):hover td {
  background-color: #ffffff !important;
}
.tenant-manager-table >>> .tenant-users-table-no-hover tbody tr:nth-of-type(even):hover > td,
.tenant-manager-table >>> .tenant-users-table-no-hover tbody tr:nth-of-type(even):hover td,
.tenant-users-table-no-hover tbody tr:nth-of-type(even):hover td {
  background-color: rgba(144, 238, 144, 0.5) !important;
}
.tenant-manager-table >>> tbody tr:hover .tenant-users-table-no-hover tbody tr:nth-of-type(odd) td {
  background-color: #ffffff !important;
}
.tenant-manager-table >>> tbody tr:hover .tenant-users-table-no-hover tbody tr:nth-of-type(even) td {
  background-color: rgba(144, 238, 144, 0.5) !important;
}
.tenant-users-table .tenant-user-username {
  max-width: none;
  min-width: 160px;
  overflow: visible;
  text-overflow: clip;
}
.tenant-users-table .tenant-user-name {
  max-width: 140px;
}
.tenant-users-table .tenant-user-login {
  max-width: 160px;
}
td {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  max-width: 330px;
}
</style>

